import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import { 
  Brain, 
  Sparkle, 
  ShieldCheck, 
  Stethoscope,
  InstagramLogo,
  TwitterLogo,
  LinkedinLogo
} from '@phosphor-icons/react';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

import Header from '../components/landing/Header';

const LandingPage = () => {
  const heroSlides = [
    {
      id: 1,
      text: "پوستتو نمی‌شناسی؟ درمووایز می‌شناستش.",
      color: "#e8f4fd"
    },
    {
      id: 2,
      text: "فقط یه اسکن... تا بفهمی چی برای پوستت خوبه",
      color: "#f0f8ff"
    },
    {
      id: 3,
      text: "روتین مراقبتی مخصوص خودت",
      color: "#f5f0ff"
    },
    {
      id: 4,
      text: "محصول جدید خریدی؟ بده درمووایز چک کنه",
      color: "#fff0f5"
    }
  ];

  const features = [
    {
      id: 1,
      icon: Brain,
      title: "آنالیز دقیق پوست با هوش مصنوعی",
      description: "با استفاده از پیشرفته‌ترین الگوریتم‌های هوش مصنوعی، پوست شما را به دقت تحلیل می‌کنیم"
    },
    {
      id: 2,
      icon: Sparkle,
      title: "ساخت روتین مخصوص تو",
      description: "بر اساس نوع پوست و نیازهای شما، روتین مراقبتی شخصی‌سازی شده ارائه می‌دهیم"
    },
    {
      id: 3,
      icon: ShieldCheck,
      title: "بررسی محصولات جدید",
      description: "قبل از خرید، محصولات جدید را با هوش مصنوعی بررسی کنید"
    },
    {
      id: 4,
      icon: Stethoscope,
      title: "بدون نیاز به دکتر رفتن",
      description: "برای مشکلات پوستی ساده، بدون مراجعه به پزشک راهنمایی دریافت کنید"
    }
  ];

  return (
    <>
      <Header />
      
      <div className="min-h-screen bg-pastel-blue font-vazirmatn" dir="rtl">
        {/* Hero Slider Section */}
        <section className="relative h-screen flex items-center justify-center overflow-hidden">
          <Swiper
            modules={[Autoplay, Pagination, Navigation]}
            spaceBetween={0}
            slidesPerView={1}
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
            }}
            pagination={{
              clickable: true,
              bulletActiveColor: '#4f46e5',
            }}
            navigation={{
              nextEl: '.swiper-button-next',
              prevEl: '.swiper-button-prev',
            }}
            loop={true}
            className="w-full h-full"
          >
            {heroSlides.map((slide) => (
              <SwiperSlide key={slide.id}>
                <div 
                  className="flex items-center justify-center h-full px-4 sm:px-6 lg:px-8"
                  style={{ backgroundColor: slide.color }}
                >
                  {/* Mobile Layout */}
                  <div className="flex flex-col lg:flex-row items-center w-full h-full">
                    {/* Image Content - Full Width on Mobile, Half on Desktop */}
                    <motion.div
                      initial={{ opacity: 0, x: -50 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8 }}
                      className="w-full lg:w-1/2 h-1/2 lg:h-full"
                    >
                      {slide.id === 1 ? (
                        <img 
                          src="/slide1.png" 
                          alt="Slide 1" 
                          className="w-full h-full object-cover"
                        />
                      ) : slide.id === 2 ? (
                        <img 
                          src="/slide2.png" 
                          alt="Slide 2" 
                          className="w-full h-full object-cover"
                        />
                      ) : slide.id === 3 ? (
                        <img 
                          src="/slide3.png" 
                          alt="Slide 3" 
                          className="w-full h-full object-cover"
                        />
                      ) : slide.id === 4 ? (
                        <img 
                          src="/slide4.png" 
                          alt="Slide 4" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div 
                          className="w-full h-full flex items-center justify-center"
                          style={{ backgroundColor: slide.color.replace('0.9', '0.7') }}
                        >
                          <div className="text-gray-500 text-2xl font-bold">
                            تصویر {slide.id}
                          </div>
                        </div>
                      )}
                    </motion.div>

                    {/* Text Content - Full Width on Mobile, Half on Desktop */}
                    <motion.div
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                      className="w-full lg:w-1/2 h-1/2 lg:h-full flex items-center justify-center px-4 sm:px-8 lg:px-16 py-6 lg:py-0"
                    >
                      <div className="text-center lg:text-right w-full max-w-lg">
                        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-800 mb-4 lg:mb-6 xl:mb-8 leading-tight">
                          {slide.text}
                        </h1>

                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="mt-4 lg:mt-6"
                        >
                          <Link
                            to="/login"
                            className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 shadow-lg hover:shadow-xl"
                          >
                            شروع کنید
                          </Link>
                        </motion.div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </section>

        {/* Features Section */}
        <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-12 lg:mb-16"
            >
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-3 lg:mb-4">
                چرا درمووایز؟
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
                با استفاده از تکنولوژی‌های پیشرفته، تجربه‌ای متفاوت در مراقبت از پوست ارائه می‌دهیم
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                      <feature.icon size={24} className="sm:w-8 sm:h-8 text-indigo-600" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2 sm:mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-4 lg:mb-6">
                همه اینا فقط با یه کلیک
              </h2>
              <p className="text-lg sm:text-xl text-gray-600 mb-6 lg:mb-8 px-4">
                آنالیز پوستت رو الان شروع کن
              </p>
              
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/login"
                  className="inline-flex items-center px-8 sm:px-10 py-4 sm:py-5 text-lg sm:text-xl font-medium rounded-full text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  شروع کن
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#f5f5f5] py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 lg:gap-8">
              {/* Logo and Description */}
              <div className="col-span-1 md:col-span-2">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">درمووایز</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4">
                  هوشمندترین دستیار مراقبت از پوست شما با استفاده از تکنولوژی هوش مصنوعی
                </p>
                <div className="flex space-x-4 space-x-reverse">
                  <motion.a
                    href="#"
                    whileHover={{ scale: 1.1 }}
                    className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-full flex items-center justify-center hover:bg-indigo-100 transition-colors duration-200"
                  >
                    <InstagramLogo size={16} className="sm:w-5 sm:h-5 text-gray-600" />
                  </motion.a>
                  <motion.a
                    href="#"
                    whileHover={{ scale: 1.1 }}
                    className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-full flex items-center justify-center hover:bg-indigo-100 transition-colors duration-200"
                  >
                    <TwitterLogo size={16} className="sm:w-5 sm:h-5 text-gray-600" />
                  </motion.a>
                  <motion.a
                    href="#"
                    whileHover={{ scale: 1.1 }}
                    className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-full flex items-center justify-center hover:bg-indigo-100 transition-colors duration-200"
                  >
                    <LinkedinLogo size={16} className="sm:w-5 sm:h-5 text-gray-600" />
                  </motion.a>
                </div>
              </div>

              {/* Quick Links - Temporarily Disabled */}
              {/* <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-4">لینک‌های مفید</h4>
                <ul className="space-y-2">
                  <li>
                    <Link to="#" className="text-gray-600 hover:text-indigo-600 transition-colors duration-200">
                      درباره ما
                    </Link>
                  </li>
                  <li>
                    <Link to="#" className="text-gray-600 hover:text-indigo-600 transition-colors duration-200">
                      قوانین
                    </Link>
                  </li>
                  <li>
                    <Link to="#" className="text-gray-600 hover:text-indigo-600 transition-colors duration-200">
                      تماس با ما
                    </Link>
                  </li>
                </ul>
              </div> */}

              {/* Contact Info */}
              <div>
                <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4">تماس</h4>
                <p className="text-sm sm:text-base text-gray-600">
                  ایمیل: info@dermowise.ir
                </p>
              </div>
            </div>

            <div className="border-t border-gray-200 mt-6 lg:mt-8 pt-6 lg:pt-8 text-center">
              <p className="text-xs sm:text-sm text-gray-500">
                تولید شده با عشق در ایران
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingPage; 